import { Config } from '../config'
import { Cookies, getCookie } from '../utils/cookies'
import { popularStyles } from '../utils/styles'
import { authCall, request } from './rcaApiMethods'

/**
 * API wrapper for api[.playground].citation-api.com
 * and csl[.playground].citation-api.com
 */
class FormatApi {
  endpoints: {
    format: {
      url: string
      header: {}
    }
    popularStyles: {
      url: string
      header: {}
    }
    styles: {
      url: string
      header: {}
    }
  }

  apiKey: string

  headers: {}

  /**
   * Create a new format api client
   * @param {Object} endpoints the urls for the api and styles server
   * @param {string} apiKey the api key needed for access
   * @param {Object} headers - optional headers to add to FormatApi requests
   */
  constructor(endpoints, apiKey, headers) {
    /**
     * the url for the api and styles server
     */
    this.endpoints = endpoints
    /**
     * the api key needed for access
     */
    this.apiKey = apiKey
    /**
     * additional headers to add to requests
     */
    this.headers = headers
  }

  /**
   * Set the api key
   * @param {string} apiKey the new api key to use
   */
  setApiKey(apiKey) {
    this.apiKey = apiKey
  }

  /**
   * Set the endpoints
   * @param {Object} endpoints the new endpoints to use
   */
  setEndpoints(endpoints) {
    this.endpoints = endpoints
  }

  /**
   * Format a list of citations in a certain style
   * @param {Object[]} citations the citations to format
   * @param {string} citationStyle the citation style to use for formating
   * @param {boolean} inline true, if the formated citations should be an inline citations
   * @return {Promise<Object, Object>} the formated citations or error if failed
   */
  formatCitations(citations, citationStyle, inline = false) {
    const customIdCitations = []
    citations.forEach((citation) => {
      const customIdCitation = citation
      customIdCitation.custom_id = citation.id
      customIdCitations.push(customIdCitation)
    })
    const apiData = {
      key: this.apiKey,
      style: citationStyle,
      bulk: customIdCitations,
      inline,
    }
    return request(this.endpoints.format.url, 'PUT', apiData, {
      ...this.endpoints.format.header,
      ...this.headers,
    }) as any
  }

  /**
   * Get the most popular styles
   * @return {Promise<Object, Object>} the popular styles or error if failed
   */
  getPopularStyles() {
    return request(
      this.endpoints.popularStyles.url,
      'GET',
      false,
      Object.assign(this.endpoints.popularStyles.header, this.headers)
    ) as any
  }

  /**
   * Get styles for a given search term (with paging)
   * @param {string} term the term to be matched. Empty to get all styles
   * @param {number} page the page to get
   * @return {Promise<Object, Object>} the matching styles or error if failed
   */
  getStyles(term, page) {
    const searchTerm = term || ''
    const stylePage = page || 1
    return authCall(
      `${this.endpoints.styles.url}?query=${searchTerm}&page=${stylePage}`,
      undefined,
      undefined,
      this.headers
    ) as any
  }
}

const headers = {
  'X-AlCell': '',
}

export const setHeaderCookieName = () => {
  headers['X-AlCell'] =
    typeof window !== 'undefined' ? getCookie(Cookies.AdLayout) || '' : ''
}

export const formatApi = new FormatApi(
  {
    format: { url: `${Config.apiUrl}/bulk` },
    // TODO: this is where the styles API is configured...
    // it's just /api/styles in CFE
    // styles: { url: `/api/styles` },
    styles: { url: `${Config.styleUrl}` },
  },
  Config.apiKey,
  headers
)

export const getUrlStyleName = async (style: string): Promise<[string, string]> => {
  const pop = popularStyles[style]
  if (pop) {
    return pop
  }
  try {
    const styleName = await formatApi.getStyles(style, 1)
    const { name } = styleName.results[0]
    return [name, name]
  } catch {
    const name = style.toUpperCase()
    return [name, name]
  }
}
